"use client"

import styled from "styled-components"
import {
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts"
import {
  Card,
  CardHeader,
  Sparkline,
  Subtitle,
  Title,
  TitleGroup,
} from "./primitives"

const indicators = [
  { label: "CPI (YoY)", value: "2.9%", trend: [3.7, 3.4, 3.2, 3.1, 3.0, 2.9], down: true },
  { label: "Unemployment", value: "4.1%", trend: [3.7, 3.8, 3.9, 4.0, 4.1, 4.1], down: false },
  { label: "GDP (QoQ)", value: "2.8%", trend: [2.1, 3.0, 2.4, 3.1, 2.9, 2.8], down: false },
  { label: "Fed funds", value: "4.50%", trend: [5.5, 5.5, 5.25, 5.0, 4.75, 4.5], down: true },
]

const Grid = styled.div`
  margin-top: 1.25rem;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.625rem;
`

const Cell = styled.div`
  border-radius: 1rem;
  background: color-mix(in oklch, var(--color-muted) 50%, transparent);
  border: 1px solid color-mix(in oklch, var(--color-border) 50%, transparent);
  padding: 0.75rem 0.875rem;
`

const CellLabel = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: var(--color-muted-foreground);
`

const CellBottom = styled.div`
  margin-top: 0.375rem;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
`

const CellValue = styled.span`
  font-family: var(--font-mono);
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-card-foreground);
`

const yieldCurve = [
  { t: "3M", y: 4.6 },
  { t: "2Y", y: 4.2 },
  { t: "5Y", y: 4.1 },
  { t: "10Y", y: 4.3 },
  { t: "30Y", y: 4.5 },
]

const ChartWrap = styled.div`
  margin-top: 1.25rem;
  height: 7rem;
  width: 100%;
`

export function MacroIndicatorsCard() {
  return (
    <Card>
      <CardHeader>
        <TitleGroup>
          <Title>Economic indicators</Title>
          <Subtitle>United States · latest prints</Subtitle>
        </TitleGroup>
      </CardHeader>

      <Grid>
        {indicators.map((i) => (
          <Cell key={i.label}>
            <CellLabel>{i.label}</CellLabel>
            <CellBottom>
              <CellValue>{i.value}</CellValue>
              <Sparkline
                points={i.trend}
                width={44}
                height={20}
                color={i.down ? "var(--color-optimal)" : "var(--color-caution)"}
              />
            </CellBottom>
          </Cell>
        ))}
      </Grid>
    </Card>
  )
}

export function YieldCurveCard() {
  return (
    <Card>
      <CardHeader>
        <TitleGroup>
          <Title>Treasury yield curve</Title>
          <Subtitle>Constant maturity · %</Subtitle>
        </TitleGroup>
      </CardHeader>

      <ChartWrap>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={yieldCurve} margin={{ top: 10, right: 12, bottom: 0, left: -18 }}>
            <XAxis
              dataKey="t"
              tickLine={false}
              axisLine={false}
              tick={{ fill: "var(--color-muted-foreground)", fontSize: 11 }}
              dy={6}
            />
            <YAxis
              domain={[3.8, 4.8]}
              tickLine={false}
              axisLine={false}
              tick={{ fill: "var(--color-muted-foreground)", fontSize: 11 }}
              ticks={[4.0, 4.4, 4.8]}
            />
            <ReferenceLine y={4.2} stroke="var(--color-border)" strokeDasharray="3 3" />
            <Line
              type="monotone"
              dataKey="y"
              stroke="var(--color-optimal)"
              strokeWidth={3}
              dot={{ r: 3, fill: "var(--color-optimal)", strokeWidth: 0 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartWrap>
    </Card>
  )
}

export default function MacroCards() {
  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <MacroIndicatorsCard />
      <YieldCurveCard />
    </div>
  )
}
